import { isPasswordValid } from '@creator/sdk/modules/account/account.helpers';
import type { FirebaseUserCredential } from '@creator/sdk/modules/account/account.model';
import Button from '@creator/ui/components/Button/ButtonV2';
import Field, { type FieldProps } from '@creator/ui/components/Form/Field/Field';
import Text from '@creator/ui/components/Text/Text';
import useForm from '@src/hooks/use-form';
import { changePassword } from '@src/model/auth';
import { noop } from '@src/utils/utils';
import { useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

export interface ForgotPasswordProps {
    className?: string;
    userId: string;
    displayName: string;
    firebaseUser: FirebaseUserCredential;
    onSuccess?: () => void;
}

interface Form {
    displayName: string;
    password: string;
    confirmPassword: string;
}

const ForgotPassword: FC<ForgotPasswordProps> = props => {
    const { onSuccess = noop, className, firebaseUser, displayName, userId } = props;
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);

    const { setValue, getValue, setError, getError, clearErrors } = useForm<Form>(getInitialValues());

    function getInitialValues(): Form {
        return {
            displayName: displayName,
            password: '',
            confirmPassword: '',
        };
    }

    function mapError(error) {
        if (error.message === 'invalidPassword') return setError('password', error.message);

        alert(`${t('generalErrorMessage')} ${error.message}`);
    }

    function validateForm(): undefined | boolean {
        if (!isPasswordValid(getValue('password'))) return setError('password', 'invalidPassword');
        if (getValue('password') !== getValue('confirmPassword')) return setError('confirmPassword', 'invalidConfirmPassword');

        return true;
    }

    async function onSubmit(): Promise<void> {
        clearErrors();

        const isValid = validateForm();
        if (!isValid) return;

        setIsLoading(true);
        try {
            await changePassword({ userId, password: getValue('password'), firebaseUser });
            onSuccess();
        } catch (error) {
            console.error(error);
            mapError(error);
            setIsLoading(false);
        }
    }

    function onEnterPress(): void {
        if (!getValue('displayName') || !getValue('password')) return;

        onSubmit();
    }

    function getErrorMessage(field: keyof Form): string {
        const errorCode = getError(field);
        if (!errorCode) return '';

        return t(errorCode);
    }

    function renderdisplayNameField() {
        const fieldProps: FieldProps = {
            inputProps: {
                autoComplete: 'username',
                onChange: (e): void => setValue('displayName', e.target.value),
                placeholder: t('forgotPassword.displayNamePlaceholder'),
                value: getValue('displayName'),
                onEnterPress: onEnterPress,
                pattern: '^[a-zA-Z0-9_]{0,20}$',
                isDisabled: true,
            },
            fieldLaelProps: {
                label: t('forgotPassword.displayNameFieldLabel'),
            },

            error: getErrorMessage('displayName'),
        };

        return <Field {...fieldProps} />;
    }

    function renderPasswordField() {
        const fieldProps: FieldProps = {
            className: 'mt-4 lg:mt-6',
            inputProps: {
                autoComplete: 'new-password',
                type: 'password',
                onChange: (e): void => setValue('password', e.target.value),
                placeholder: t('forgotPassword.passwordPlaceholder'),
                onEnterPress: onEnterPress,
                value: getValue('password'),
            },
            fieldLaelProps: {
                label: t('forgotPassword.passwordFieldLabel'),
                helpTipText: t('createPasswordHelpTip'),
                helpTipTextProps: {
                    enableMultiline: true,
                },
            },
            error: getErrorMessage('password'),
        };

        return <Field {...fieldProps} />;
    }

    function renderConfirmPasswordField() {
        const fieldProps: FieldProps = {
            className: 'mt-4 lg:mt-6',
            inputProps: {
                autoComplete: 'new-password',
                type: 'password',
                onChange: (e): void => setValue('confirmPassword', e.target.value),
                placeholder: t('forgotPassword.confirmPasswordPlaceholder'),
                onEnterPress: onEnterPress,
                value: getValue('confirmPassword'),
            },
            fieldLaelProps: {
                label: t('forgotPassword.confirmPasswordFieldLabel'),
            },
            error: getErrorMessage('confirmPassword'),
        };

        return <Field {...fieldProps} />;
    }

    return (
        <div onSubmit={onSubmit} className={`flex flex-col ${className}`}>
            {renderdisplayNameField()}
            {renderPasswordField()}
            {renderConfirmPasswordField()}
            <Button isLoading={isLoading} isDisabled={!displayName || !getValue('password') || !getValue('confirmPassword')} className="mt-4 lg:mt-6" onClick={onSubmit}>
                <Text>{t('forgotPassword.submit')}</Text>
            </Button>
        </div>
    );
};

export default ForgotPassword;
